import { DataGrid, GridColDef } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

import { PostGreSQLInterval } from 'api';
import { GetPackageResult, useGetPackage } from 'api/hooks/private/packages/GetPackage';

import { formatCents } from 'utils/formatCurrency';

import PackageCourseListFooter, { PackageCourseListFooterProps } from './PackageCourseListFooter';

declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides extends PackageCourseListFooterProps { }
}

dayjs.extend(duration);
dayjs.extend(relativeTime);

function formatTimeIncluded(interval: PostGreSQLInterval | null): string {
  if (!interval) return 'None';

  const timeIncluded = dayjs.duration(interval);

  // if interval is empty or 0
  if (timeIncluded.asMilliseconds() === 0) return 'None';

  // if duration is 60 minutes or less, format as minutes
  if (timeIncluded.asMinutes() <= 60) {
    return `${timeIncluded.asMinutes()} Minute${timeIncluded.asMinutes() !== 1 ? 's' : ''}`;
  }
  return `${timeIncluded.asHours()} Hour${timeIncluded.asHours() !== 1 ? 's' : ''}`;
}

const columns: GridColDef<GetPackageResult['package_courses'][number]>[] = [
  {
    field: 'title',
    type: 'string',
    headerName: 'Title',
    minWidth: 300,
    valueGetter: (_, row) => row.course.title,
  },
  {
    field: 'course_type',
    type: 'singleSelect',
    headerName: 'Type',
    minWidth: 150,
    valueGetter: (_, row) => row.course.course_type,
    valueOptions: [
      {
        value: 'behind_the_wheel',
        label: 'Behind The Wheel',
      },
      {
        value: 'in_person',
        label: 'In Person',
      },
      {
        value: 'synchronous_online',
        label: 'Synchronous Online',
      },
      {
        value: 'fully_online',
        label: 'Fully Online',
      },
    ],
  },
  {
    field: 'schedule_type',
    type: 'singleSelect',
    headerName: 'Schedule',
    minWidth: 150,
    valueGetter: (_, row) => row.course.schedule_type,
    valueOptions: [
      {
        value: 'self_scheduled',
        label: 'Student Scheduled',
      },
      {
        value: 'fixed',
        label: 'Fixed Schedule',
      },
      {
        value: 'manual',
        label: 'Manual Scheduling',
      },
      {
        value: 'no_schedule',
        label: 'No Schedule',
      },
    ],
  },
  {
    field: 'performance_time_included',
    type: 'string',
    headerName: 'Performance Time Included',
    minWidth: 200,
    valueGetter: (_, row) => formatTimeIncluded(row.performance_time_included),
  },
  {
    field: 'observation_time_included',
    type: 'string',
    headerName: 'Observation Time Included',
    minWidth: 200,
    valueGetter: (_, row) => formatTimeIncluded(row.observation_time_included),
  },
  {
    field: 'deposit',
    type: 'number',
    headerName: 'Deposit',
    minWidth: 200,
    valueGetter: (_, row) => row.deposit / 100,
    valueFormatter: (_, row) => formatCents(row.deposit),
  },
  {
    field: 'cost',
    type: 'number',
    headerName: 'Price',
    minWidth: 200,
    valueGetter: (_, row) => row.cost / 100,
    valueFormatter: (_, row) => formatCents(row.cost),
  },
];

export interface PackageCourseListProps {
  packageId: string;
}

export function PackageCourseList({ packageId }: PackageCourseListProps) {
  // get package
  const pkg = useGetPackage({
    variables: {
      id: packageId,
    },
    enabled: !!packageId,
  });

  return (
    <DataGrid
      columns={columns}
      rows={pkg.data?.package_courses}
      getRowId={(row) => row.course_id}
      loading={pkg.isFetching}
      slots={{
        footer: PackageCourseListFooter,
      }}
      slotProps={{
        footer: {
          total: formatCents(
            pkg.data?.package_courses.reduce((accumulator, course) => accumulator + course.cost, 0) ?? 0,
          ),
        },
      }}
    />
  );
}

export default PackageCourseList;
